import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { verifyTwoFactor } from '@/api/auth.api';
import { completeLoginSession } from '@/lib/auth/complete-login-session';

/**
 * Step 2 of login (FR-04): exchange the second-factor code for the session.
 *
 * This is where the post-login side effects that used to live in `useLogin`
 * now happen - persist the session token, seed the auth cache, and land the
 * user - because step 1 no longer issues a token.
 *
 * @param preAuthToken - the step-1 credential, held in router/component state
 * by the caller. Never persisted, never passed to `setAccessToken`.
 * @param returnTo - where to land instead of the dashboard (e.g. back to an
 * invite-accept page). The caller is responsible for having already validated
 * it with `isSafeReturnTo`.
 */
export function useVerifyTwoFactor(preAuthToken: string, returnTo?: string) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { code: string }) =>
      verifyTwoFactor({ code: payload.code, preAuthToken }),
    onSuccess: (result) => completeLoginSession(result, { queryClient, navigate, returnTo }),
  });
}
